import React, { useEffect, useRef, useState } from 'react';
import { getTurnstileConfig, checkIpExempt } from '../api';

interface TurnstileProps {
  onVerify: (token: string) => void;
  onError?: (error: string) => void;
  onExpire?: () => void;
  className?: string;
}

interface TurnstileRef {
  reset: () => void;
}

// Глобальные типы для Turnstile
declare global {
  interface Window {
    turnstile?: {
      render: (element: HTMLElement, options: {
        sitekey: string;
        callback: (token: string) => void;
        'error-callback'?: (error: string) => void;
        'expired-callback'?: () => void;
        theme?: 'light' | 'dark' | 'auto';
        size?: 'normal' | 'compact';
      }) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

export const Turnstile = React.forwardRef<TurnstileRef, TurnstileProps>(
  ({ onVerify, onError, onExpire, className }, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExempt, setIsExempt] = useState(false);
  const [siteKey, setSiteKey] = useState<string>('');
  const [error, setError] = useState<string>('');

  // Загрузка скрипта Turnstile
  const loadTurnstileScript = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      // Проверяем, не загружен ли уже скрипт
      if (window.turnstile) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.defer = true;
      
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Turnstile script'));
      
      document.head.appendChild(script);
    });
  };

  // Инициализация Turnstile
  const initializeTurnstile = async () => {
    try {
      setIsLoading(true);
      setError('');

      // Проверяем конфигурацию и IP
      const [config, ipCheck] = await Promise.all([
        getTurnstileConfig().catch(err => {
          console.error('Error getting Turnstile config:', err);
          return { enabled: false, siteKey: null };
        }),
        checkIpExempt().catch(err => {
          console.error('Error checking IP exempt:', err);
          return { exempt: false, ip: null };
        })
      ]);

      // Если IP в исключениях, автоматически вызываем onVerify с тестовым токеном
      if (ipCheck.exempt) {
        setIsExempt(true);
        onVerify('test-token');
        setIsLoading(false);
        return;
      }

      if (!config.enabled || !config.siteKey) {
        // Если Turnstile отключен, используем тестовый токен
        onVerify('test-token');
        setIsLoading(false);
        return;
      }

      // Проверяем, является ли это тестовым ключом
      const isTestKey = config.siteKey.startsWith('0x4AAAAAAA');
      if (isTestKey) {
        // Для тестового ключа автоматически выдаем токен без рендеринга виджета
        setIsExempt(true); // Показываем как исключение
        onVerify('test-token');
        setIsLoading(false);
        return;
      }

      setSiteKey(config.siteKey);

      // Загружаем скрипт Turnstile
      await loadTurnstileScript();

      // Рендерим виджет
      if (containerRef.current && window.turnstile) {
        const container = containerRef.current;
        if (container instanceof HTMLElement) {
          widgetIdRef.current = window.turnstile.render(container, {
            sitekey: config.siteKey,
            callback: (token: string) => {
              onVerify(token);
            },
            'error-callback': (error: string) => {
              setError('Ошибка проверки безопасности');
              onError?.(error);
            },
            'expired-callback': () => {
              onExpire?.();
            },
            theme: 'auto',
            size: 'normal',
          });
        } else {
          throw new Error('Container element is not valid');
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Ошибка инициализации Turnstile';
      setError(errorMessage);
      onError?.(errorMessage);
      
      // В случае ошибки используем тестовый токен
      onVerify('test-token');
    } finally {
      setIsLoading(false);
    }
  };

  // Сброс виджета
  const reset = () => {
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
    }
  };

  useEffect(() => {
    // Небольшая задержка, чтобы убедиться, что DOM готов
    const timer = setTimeout(() => {
      initializeTurnstile();
    }, 100);

    // Очистка при размонтировании
    return () => {
      clearTimeout(timer);
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }
    };
  }, []);

  // Экспонируем метод reset через ref
  React.useImperativeHandle(ref, () => ({
    reset,
  }));

  if (isExempt) {
    return (
      <div className={`text-sm text-green-600 ${className}`}>
        ✓ Проверка безопасности пройдена (тестовый режим)
      </div>
    );
  }

  if (error) {
    return (
      <div className={`text-sm text-destructive ${className}`}>
        {error}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={`text-sm text-muted-foreground ${className}`}>
        Загрузка проверки безопасности...
      </div>
    );
  }

  return (
    <div className={className}>
      <div ref={containerRef} />
    </div>
  );
});

Turnstile.displayName = 'Turnstile';
